import {
  ProductListQuery,
  ProductOrderByWithRelationInput,
  ProductWhereInput,
  QueryMode,
  SortOrder,
} from '@/__generated__/graphql';
import { useMutation, useQuery } from '@apollo/client';
import {
  ActionIcon,
  Badge,
  Button,
  DefaultMantineColor,
  Group,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue, useLocalStorage } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import {
  IconBolt,
  IconCheck,
  IconEdit,
  IconPencil,
  IconPlus,
  IconPrinter,
  IconRefresh,
  IconSearch,
  IconSum,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useMemo, useState } from 'react';
import { BulkPriceEditModal } from '../components/BulkPriceEditModal';
import { BulkProductDetailsEditModal } from '../components/BulkProductDetailsEditModal';
import { ProductUpsertModal } from '../components/ProductUpsertModal';
import { ProductVariantsTable } from '../components/ProductVariantsTable';
import { countProductsDocument } from '../graphql/count-products';
import { deleteProductDocument } from '../graphql/delete-product';
import { productListDocument, productLivesDocument } from '../graphql/get-products';
import { setProductActiveDocument, setProductCanRemoveDocument } from '../graphql/mutations';
import dayjs from 'dayjs';
import { ProductFindModal } from '../components/ProductFindModal';

export const ProductList = () => {
  const [filterStatus, setFilterStatus] = useLocalStorage<ProductWhereInput>({
    key: 'product-filterStatus',
    defaultValue: {},
  });

  const [where] = useDebouncedValue(filterStatus, 500);

  const [selected, setSelected] = useState<ProductListQuery['products']>([]);

  const [sortStatus, setSortStatus] = useLocalStorage<
    DataTableSortStatus<ProductListQuery['products'][number]>
  >({
    key: 'product-sortStatus',
    defaultValue: {
      columnAccessor: 'updatedAt',
      direction: 'desc',
    },
  });

  const orderBy = useMemo<ProductOrderByWithRelationInput>(() => {
    switch (sortStatus.columnAccessor) {
      case 'description':
        return { description: { sort: sortStatus.direction as SortOrder } };
      default:
        return { [sortStatus.columnAccessor]: sortStatus.direction };
    }
  }, [sortStatus]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useLocalStorage({ key: 'product-pageSize', defaultValue: 10 });

  const { data: count } = useQuery(countProductsDocument, {
    variables: { where },
    fetchPolicy: 'network-only',
  });

  const { data, loading, refetch } = useQuery(productListDocument, {
    variables: {
      skip: (page - 1) * pageSize,
      take: pageSize,
      where,
      orderBy,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  const { data: lives } = useQuery(productLivesDocument, {
    variables: {
      orderBy: { date: SortOrder.Desc },
    },
  });

  const [setProductActive] = useMutation(setProductActiveDocument, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
  });

  const [setProductCanRemove] = useMutation(setProductCanRemoveDocument, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
  });

  const [deleteProduct] = useMutation(deleteProductDocument, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
  });

  const activeFilters: { label: string; color: DefaultMantineColor; onClear: () => void }[] = [];

  if (filterStatus.code?.contains) {
    activeFilters.push({
      label: `Kód: ${filterStatus.code.contains}`,
      color: 'gray',
      onClear: () => setFilterStatus((prev) => ({ ...prev, code: undefined })),
    });
  }

  if (filterStatus.name?.contains) {
    activeFilters.push({
      label: `Názov: ${filterStatus.name.contains}`,
      color: 'blue',
      onClear: () => setFilterStatus((prev) => ({ ...prev, name: undefined })),
    });
  }

  if (filterStatus.description?.contains) {
    activeFilters.push({
      label: `Popis: ${filterStatus.description.contains}`,
      color: 'cyan',
      onClear: () => setFilterStatus((prev) => ({ ...prev, description: undefined })),
    });
  }

  if (typeof filterStatus.price?.equals === 'number') {
    activeFilters.push({
      label: `Cena: ${filterStatus.price.equals} €`,
      color: 'orange',
      onClear: () => setFilterStatus((prev) => ({ ...prev, price: undefined })),
    });
  }

  if (typeof filterStatus.active?.equals === 'boolean') {
    activeFilters.push({
      label: `Stav: ${filterStatus.active.equals ? 'aktívny' : 'neaktívny'}`,
      color: filterStatus.active.equals ? 'teal' : 'red',
      onClear: () => setFilterStatus((prev) => ({ ...prev, active: undefined })),
    });
  }

  if (filterStatus.live?.is?.id?.equals) {
    const liveId = filterStatus.live.is.id.equals;
    const live = lives?.lives.find((l) => l.id === liveId);
    activeFilters.push({
      label: `LIVE: ${live?.name ?? liveId}`,
      color: 'grape',
      onClear: () => setFilterStatus((prev) => ({ ...prev, live: undefined })),
    });
  }

  return (
    <Stack>
      <Paper p="md" withBorder component={Stack}>
        <Group wrap="nowrap">
          <TextInput
            placeholder="Kód..."
            value={filterStatus.code?.contains ?? ''}
            onChange={(event) => {
              setFilterStatus((prev) => ({
                ...prev,
                code:
                  event.currentTarget.value.length > 0
                    ? { contains: event.currentTarget.value, mode: QueryMode.Insensitive }
                    : undefined,
              }));
            }}
          />
          <TextInput
            placeholder="Názov.."
            value={filterStatus.name?.contains ?? ''}
            onChange={(event) => {
              setFilterStatus((prev) => ({
                ...prev,
                name:
                  event.currentTarget.value.length > 0
                    ? { contains: event.currentTarget.value, mode: QueryMode.Insensitive }
                    : undefined,
              }));
            }}
          />
          <TextInput
            placeholder="Popis..."
            value={filterStatus.description?.contains ?? ''}
            onChange={(event) => {
              setFilterStatus((prev) => ({
                ...prev,
                description:
                  event.currentTarget.value.length > 0
                    ? { contains: event.currentTarget.value, mode: QueryMode.Insensitive }
                    : undefined,
              }));
            }}
          />
          <NumberInput
            placeholder="Cena..."
            value={filterStatus.price?.equals ?? ''}
            onChange={(value) => {
              setFilterStatus((prev) => ({
                ...prev,
                price: value !== '' ? { equals: +value } : undefined,
              }));
            }}
            allowNegative={false}
            allowDecimal={false}
            hideControls
          />
          <Select
            clearable
            placeholder="Stav..."
            data={['true', 'false']}
            value={
              filterStatus.active?.equals != null ? filterStatus.active.equals.toString() : null
            }
            onChange={(value) => {
              setFilterStatus((prev) => ({
                ...prev,
                active: value ? { equals: JSON.parse(value) } : undefined,
              }));
            }}
            onClear={() => {
              setFilterStatus((prev) => ({ ...prev, active: undefined }));
            }}
          />
          <Select
            clearable
            placeholder="LIVE..."
            data={lives?.lives.map((live) => ({ value: live.id, label: live.name })) ?? []}
            value={filterStatus.live?.is?.id?.equals ?? ''}
            onChange={(value) => {
              setFilterStatus((prev) => ({
                ...prev,
                live: value ? { is: { id: { equals: value } } } : undefined,
              }));
            }}
            onClear={() => {
              setFilterStatus((prev) => ({ ...prev, live: undefined }));
            }}
          />
          {selected.length > 0 && (
            <Button
              color="teal"
              component="a"
              href={`https://api.laskykrasky.sk/api/live/product?productId=${selected.map((p) => p.id).join(',')}`}
              target="_blank"
            >
              Vytlačiť ({selected.length})
            </Button>
          )}
          <ActionIcon
            size="md"
            variant="filled"
            color="indigo"
            onClick={(e) => {
              e.stopPropagation();

              modals.open({
                title: `Nájsť produkt`,
                size: 'auto',
                children: (
                  <ProductFindModal liveId={filterStatus.live?.is?.id?.equals ?? undefined} />
                ),
              });
            }}
          >
            <IconSearch size={16} />
          </ActionIcon>
        </Group>
        {activeFilters.length > 0 && (
          <Group>
            <Text size="sm" fw={500} c="dimmed">
              Aktívne filtre:
            </Text>
            {activeFilters.map((filter, index) => (
              <Badge
                key={index}
                color={filter.color}
                variant="outline"
                size="lg"
                radius="xs"
                onClick={filter.onClear}
                style={{ cursor: 'pointer' }}
              >
                {filter.label}
              </Badge>
            ))}
            <ActionIcon variant="outline" color="red" onClick={() => setFilterStatus({})}>
              <IconX size={16} />
            </ActionIcon>
          </Group>
        )}
      </Paper>
      <DataTable
        withTableBorder
        striped
        highlightOnHover
        borderRadius="sm"
        minHeight={200}
        records={data?.products ?? []}
        fetching={loading}
        totalRecords={count?.countProducts ?? 0}
        recordsPerPage={pageSize}
        page={page}
        onPageChange={setPage}
        recordsPerPageOptions={[5, 10, 25, 50]}
        onRecordsPerPageChange={setPageSize}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        selectedRecords={selected}
        onSelectedRecordsChange={setSelected}
        columns={[
          {
            accessor: 'code',
            title: 'Kód',
            sortable: true,
          },
          {
            accessor: 'name',
            title: 'Názov',
            sortable: true,
            render: (product) => (
              <Stack gap={0}>
                <Group gap={4} wrap="nowrap" align="center">
                  <Text inherit>{product.name}</Text>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="blue"
                    onClick={(e) => {
                      e.stopPropagation();
                      modals.open({
                        title: `Hromadná úprava názvu/popisu — ${product.code}`,
                        size: 'md',
                        children: (
                          <BulkProductDetailsEditModal
                            productId={product.id}
                            code={product.code}
                            currentName={product.name}
                            currentDescription={product.description ?? null}
                          />
                        ),
                      });
                    }}
                  >
                    <IconPencil size={16} />
                  </ActionIcon>
                </Group>
                <Text inherit size="sm" c="dimmed">
                  {product.description}
                </Text>
              </Stack>
            ),
          },
          {
            accessor: 'price',
            title: 'Cena',
            sortable: true,
            render: (product) => (
              <Group gap="xs" wrap="nowrap">
                <Text inherit>{product.price} €</Text>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="orange"
                  onClick={(e) => {
                    e.stopPropagation();
                    modals.open({
                      title: `Hromadná úprava ceny — ${product.name}`,
                      size: 'md',
                      children: (
                        <BulkPriceEditModal
                          productId={product.id}
                          code={product.code}
                          name={product.name}
                          currentPrice={product.price}
                        />
                      ),
                    });
                  }}
                >
                  <IconPencil size={16} />
                </ActionIcon>
              </Group>
            ),
          },
          {
            accessor: 'active',
            title: 'Stav',
            render: ({ id, active }) => (
              <ActionIcon
                size="sm"
                variant="subtle"
                color={active ? 'teal' : 'red'}
                onClick={async (e) => {
                  e.stopPropagation();

                  await setProductActive({ variables: { id, active: !active } });

                  if (!active === true)
                    window
                      .open(`https://api.laskykrasky.sk/api/live/product?productId=${id}`, '_blank')
                      ?.focus();
                }}
              >
                {active ? <IconCheck size={16} /> : <IconX size={16} />}
              </ActionIcon>
            ),
            sortable: true,
          },
          {
            accessor: 'canRemove',
            title: 'Rušenie?',
            render: ({ id, canRemove }) => (
              <ActionIcon
                size="sm"
                variant="subtle"
                color={canRemove ? 'teal' : 'red'}
                onClick={async (e) => {
                  e.stopPropagation();
                  await setProductCanRemove({ variables: { id, canRemove: !canRemove } });
                }}
              >
                {canRemove ? <IconCheck size={16} /> : <IconX size={16} />}
              </ActionIcon>
            ),
            sortable: true,
          },
          {
            accessor: 'updatedAt',
            title: 'Dátum',
            render: ({ createdAt, updatedAt }) => (
              <Stack gap={0}>
                <Text inherit>{dayjs(createdAt).format('DD.MM.YYYY HH:mm:ss')}</Text>
                {updatedAt > createdAt && (
                  <Text inherit c="dimmed" size="sm">
                    {dayjs(updatedAt).format('DD.MM.YYYY HH:mm:ss')}
                  </Text>
                )}
              </Stack>
            ),
            sortable: true,
          },
          {
            accessor: 'actions',
            textAlign: 'right',
            title: (
              <Group>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="green"
                  onClick={(e) => {
                    e.stopPropagation();

                    void refetch();
                  }}
                >
                  <IconRefresh size={16} />
                </ActionIcon>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  onClick={(e) => {
                    e.stopPropagation();

                    modals.open({
                      title: `Vytvoriť nový produkt`,
                      size: '80%',
                      closeOnClickOutside: false,
                      children: (
                        <ProductUpsertModal
                          liveId={filterStatus.live?.is?.id?.equals ?? undefined}
                          defaultValues={{
                            canRemove: false,
                          }}
                        />
                      ),
                    });
                  }}
                >
                  <IconPlus size={16} />
                </ActionIcon>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="indigo"
                  onClick={(e) => {
                    e.stopPropagation();

                    modals.open({
                      title: `Vytvoriť nový produkt (bleskovky)`,
                      size: '80%',
                      closeOnClickOutside: false,
                      children: (
                        <ProductUpsertModal
                          liveId={filterStatus.live?.is?.id?.equals ?? undefined}
                          defaultValues={{
                            canRemove: false,
                            active: true,
                            variants: [{ stock: 1 }],
                          }}
                        />
                      ),
                    });
                  }}
                  ml="auto"
                >
                  <IconBolt size={16} />
                </ActionIcon>
              </Group>
            ),
            render: (product) => (
              <Group>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  onClick={(e) => {
                    e.stopPropagation();
                    modals.open({
                      title: `Upraviť produkt ${product.id}`,
                      children: <ProductUpsertModal productId={product.id} />,
                      size: '80%',
                      closeOnClickOutside: false,
                    });
                  }}
                  disabled={product.active}
                >
                  <IconEdit size={16} />
                </ActionIcon>
                <ActionIcon
                  variant="subtle"
                  color="teal"
                  component="a"
                  href={`https://api.laskykrasky.sk/api/live/product?productId=${product.id}`}
                  target="_blank"
                >
                  <IconPrinter size={16} />
                </ActionIcon>
                <Tooltip
                  label="Pri aktívnom produkte sa môže predať viac kusov počas tlače"
                  disabled={!product.active}
                >
                  <ActionIcon
                    variant="subtle"
                    color="indigo"
                    component={product.active ? 'button' : 'a'}
                    href={
                      product.active
                        ? undefined
                        : `https://api.laskykrasky.sk/api/product/soldVariants?productId=${product.id}`
                    }
                    target={product.active ? undefined : '_blank'}
                    disabled={product.active}
                  >
                    <IconSum size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Aktívny produkt sa nedá zmazať" disabled={!product.active}>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="red"
                    disabled={product.active}
                    onClick={(e) => {
                      e.stopPropagation();

                      modals.openConfirmModal({
                        title: 'Zmazať produkt?',
                        children: `Naozaj chcete zmazať produkt ${product.id}?`,
                        labels: { cancel: 'Nie', confirm: 'Zmazať' },
                        onConfirm: () => {
                          void deleteProduct({ variables: { where: { id: product.id } } });
                        },
                      });
                    }}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            ),
          },
        ]}
        rowExpansion={{
          content: ({ record: { id } }) => <ProductVariantsTable productId={id} />,
        }}
      />
    </Stack>
  );
};
